import { AbstractControl } from '@angular/forms';
import { EMPTY, Observable, Subject, takeUntil, tap } from 'rxjs';
import { AsyncFormStateBase } from '../../../shared/state/async-form-state.base';

export abstract class SettingsRequestStateBase extends AsyncFormStateBase {
  private readonly invalidated = new Subject<void>();
  private disposed = false;
  public settingsReady = false;
  public isLoadingSettings = false;

  public get canSaveSettings(): boolean {
    return this.settingsReady && !this.isLoadingSettings && !this.isSaving;
  }

  protected loadSettings<T>(request: Observable<T>, form: AbstractControl): Observable<T> {
    if (this.disposed) return EMPTY;
    // Cancel both reads and mutation callbacks before resetting the visible scope.
    this.invalidateSettings();
    this.isLoadingSettings = true;
    form.reset(undefined, { emitEvent: false });
    form.disable({ emitEvent: false });
    return this.currentSettingsRequest(request).pipe(tap({
      next: () => {
        this.isLoadingSettings = false;
        this.settingsReady = true;
        form.enable({ emitEvent: false });
      },
      error: () => {
        this.isLoadingSettings = false;
        this.settingsReady = false;
        form.disable({ emitEvent: false });
      },
      complete: () => { this.isLoadingSettings = false; },
    }));
  }

  protected currentSettingsRequest<T>(request: Observable<T>): Observable<T> {
    return this.disposed ? EMPTY : request.pipe(takeUntil(this.invalidated));
  }

  public invalidateSettings(): void {
    this.invalidated.next();
    this.settingsReady = false;
    this.isLoadingSettings = false;
    this.isSaving = false;
    this.clearFeedback();
  }

  public disposeSettings(): void {
    this.invalidateSettings();
    this.disposed = true;
    this.invalidated.complete();
  }
}
